import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, throwError } from 'rxjs';
import { AlertHelper } from 'src/app/shared/components/helpers/alert.helpers';
import { RequestResponse } from 'src/app/shared/models/request-response.interface';
import {
  GenerarEntregaEquipoLinkPayload,
  RequestResponseEntregaEquipoLink,
  RequestResponseGetAllEntregaEquipoLink,
  RequestResponseEquiposAsignados
} from 'src/app/shared/models/entrega-equipo-link.interface';

@Injectable({
  providedIn: 'root'
})
export class EntregaEquipoLinkService {

  constructor(private http: HttpClient, private alert: AlertHelper) { }

  // POST /generar (protegido) - genera el link de entrega/devolución/reemplazo
  generar(payload: GenerarEntregaEquipoLinkPayload): Observable<RequestResponseEntregaEquipoLink> {
    return this.http
      .post<RequestResponseEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/generar`, payload)
      .pipe(
        map((res: RequestResponseEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /token/:token (pública) - sin rut (o si no coincide) solo devuelve estadoLink +
  // requiereRut, sin datos del trabajador/equipo. Con ?rut= coincidente, devuelve todo.
  obtenerPorToken(token: string, rut?: string): Observable<RequestResponseEntregaEquipoLink> {
    const query = rut ? `?rut=${encodeURIComponent(rut)}` : '';
    return this.http
      .get<RequestResponseEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/token/${token}${query}`)
      .pipe(
        map((res: RequestResponseEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /equipos-similares/:numeroSerie (pública) - aviso de posible typo antes de crear
  // un equipo nuevo (no confundir con un equipo ya existente por 1-2 caracteres de más/menos)
  buscarEquiposSimilares(numeroSerie: string): Observable<RequestResponse> {
    return this.http
      .get<RequestResponse>(`${environment.API}/rrhh/entregaEquipoLink/equipos-similares/${encodeURIComponent(numeroSerie)}`)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // POST /firmar/:token (pública, multipart/form-data: campos de archivo "firma" y "hojaFisica").
  // formData admite además, todos opcionales: nombreTrabajador, fk_equipoTecnologico (equipo ya
  // existente) o, cuando el link no trae equipo precargado, los datos de un equipo nuevo a crear
  // server-side (tipo, marca, modelo, chip, numeroSerie, accesorios, observacion — mismos campos
  // que EquipoTecnologico en equipo-tecnologico.interface.ts). El backend resuelve por numeroSerie
  // si reutiliza un equipo existente o crea uno nuevo; no se llama a equipo-tecnologico.service.ts
  // desde este flujo público porque esa ruta HTTP requiere JWT.
  firmar(token: string, formData: FormData): Observable<RequestResponseEntregaEquipoLink> {
    return this.http
      .post<RequestResponseEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/firmar/${token}`, formData)
      .pipe(
        map((res: RequestResponseEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /pendientes (protegida) - listado global de links pendientes/vencidos
  listarPendientes(): Observable<RequestResponseGetAllEntregaEquipoLink> {
    return this.http
      .get<RequestResponseGetAllEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/pendientes`)
      .pipe(
        map((res: RequestResponseGetAllEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // PUT /anular/:id (protegida)
  anular(id: number, body: any = { estadoLink: 'ANULADO' }): Observable<RequestResponse> {
    return this.http
      .put<RequestResponse>(`${environment.API}/rrhh/entregaEquipoLink/anular/${id}`, body)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /rrhh/implementos/equipos-activos-global (protegida) - listado de equipos actualmente asignados
  listarEquiposAsignados(): Observable<RequestResponseEquiposAsignados> {
    return this.http
      .get<RequestResponseEquiposAsignados>(`${environment.API}/rrhh/implementos/equipos-activos-global`)
      .pipe(
        map((res: RequestResponseEquiposAsignados) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /rrhh/implementos/equipos-activos/:fk_trabajador - equipos activos de un trabajador
  // puntual (usado para elegir cuál se devuelve al generar un REEMPLAZO).
  listarEquiposActivosDeTrabajador(fk_trabajador: number): Observable<RequestResponse> {
    return this.http
      .get<RequestResponse>(`${environment.API}/rrhh/implementos/equipos-activos/${fk_trabajador}`)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /rrhh/implementos/serie-activa/:fk_equipoTecnologico - detecta si un equipo ya
  // está activo en otra entrega antes de asignarlo de nuevo (advertencia, no bloquea).
  verificarSerieActiva(fk_equipoTecnologico: number): Observable<RequestResponse> {
    return this.http
      .get<RequestResponse>(`${environment.API}/rrhh/implementos/serie-activa/${fk_equipoTecnologico}`)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // PUT /rrhh/implementos/editar/:id (protegida) - edición admin de un movimiento de bitácora
  editarMovimiento(id: number, cambios: any): Observable<RequestResponse> {
    return this.http
      .put<RequestResponse>(`${environment.API}/rrhh/implementos/editar/${id}`, cambios)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // POST /registrar-manual (protegida, multipart) - documento ya firmado en papel, solo se sube el respaldo
  registrarManual(formData: FormData): Observable<RequestResponseEntregaEquipoLink> {
    return this.http
      .post<RequestResponseEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/registrar-manual`, formData)
      .pipe(
        map((res: RequestResponseEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // POST /registrar-forzado (protegida, JSON) - el trabajador se niega a firmar: el admin
  // cierra el movimiento igual dejando constancia del motivo, sin firma ni documento.
  registrarForzado(body: any): Observable<RequestResponseEntregaEquipoLink> {
    return this.http
      .post<RequestResponseEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/registrar-forzado`, body)
      .pipe(
        map((res: RequestResponseEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /por-movimiento/:id (protegida) - encuentra el documento firmado que originó un movimiento
  buscarPorMovimiento(fk_movimientoImplementos: number): Observable<RequestResponseEntregaEquipoLink> {
    return this.http
      .get<RequestResponseEntregaEquipoLink>(`${environment.API}/rrhh/entregaEquipoLink/por-movimiento/${fk_movimientoImplementos}`)
      .pipe(
        map((res: RequestResponseEntregaEquipoLink) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /rrhh/implementos/conflictos (protegida) - equipos activos en más de un trabajador a la vez
  listarConflictosEquipos(): Observable<RequestResponse> {
    return this.http
      .get<RequestResponse>(`${environment.API}/rrhh/implementos/conflictos`)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // POST /rrhh/implementos/incidente (protegida) - pérdida/robo/daño: cierra el equipo activo
  // y actualiza su estado en el catálogo, con motivo y observación en la bitácora.
  registrarIncidente(body: { fk_equipoTecnologico: number; fk_trabajador?: number | null; motivoIncidente: string; observacion?: string }): Observable<RequestResponse> {
    return this.http
      .post<RequestResponse>(`${environment.API}/rrhh/implementos/incidente`, body)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  // GET /rrhh/implementos/historial/:fk_trabajador (protegida) - historial completo con estadoActual
  listarHistorialPorTrabajador(fk_trabajador: number): Observable<RequestResponse> {
    return this.http
      .get<RequestResponse>(`${environment.API}/rrhh/implementos/historial/${fk_trabajador}`)
      .pipe(
        map((res: RequestResponse) => res),
        catchError((err) => this.handlerError(err))
      );
  }

  private handlerError(err: any): Observable<never> {
    let errorMessage = "";
    if (err && err.error) {
      errorMessage = `Error: ${err.error.msg}`;
      this.alert.error_small(err.error.msg);
    }
    return throwError(errorMessage);
  }

}
